/* Search palette and results page. Predictive results are rendered by sections/predictive-search; this file only fetches, swaps,
   highlights, scopes and handles the keyboard. No dependencies. */
(() => {
  const d = document;
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const track = (event, p) => { try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event }, p)); } catch (e) { /* no analytics */ } };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const RKEY = 'ase:recent-searches';
  const recent = {
    get() { try { const a = JSON.parse(localStorage.getItem(RKEY)); return Array.isArray(a) ? a.filter((x) => typeof x === 'string') : []; } catch (e) { return []; } },
    set(a) { try { localStorage.setItem(RKEY, JSON.stringify(a.slice(0, 6))); } catch (e) { /* private mode */ } },
    add(q) {
      q = String(q || '').trim();
      if (q.length < 2 || q.length > 80) return;
      this.set([q].concat(this.get().filter((x) => x.toLowerCase() !== q.toLowerCase())));
    },
    remove(q) { this.set(this.get().filter((x) => x !== q)); }
  };
  const SCOPES = { sprayer: 'product_type:"Airless sprayer"', tip: 'tag:"type:tip"', part: 'product_type:"Spare part"' };
  const LABELS = { all: 'all products', sprayer: 'sprayers', tip: 'tips', part: 'parts', guide: 'guides' };
  const searchRoot = (window.ASE && ASE.routes && ASE.routes.root) || '/';
  const searchUrl = (d.querySelector('[data-ps]') || {}).dataset?.searchUrl || searchRoot.replace(/\/$/, '') + '/search';
  const suggestUrl = (d.querySelector('[data-ps]') || {}).dataset?.suggestUrl || (window.ASE && ASE.routes.predictiveSearch) || '/search/suggest';
  /* Trade spellings the catalogue does not use. Only spellings: job words are routed in snippets/search-intent. */
  // Common misspellings from storefront searches (shop.metafields.ase.search_intents.typos, ops/search_intents.mjs), fixed
  // before the words go to Shopify so 'grayco sparyer' finds sprayers. Word for word only; multi word keys too.
  let typos = {};
  try { typos = JSON.parse((d.querySelector('[data-ps]') || {}).dataset?.typos || '{}') || {}; } catch (e) { typos = {}; }
  const fixTypos = (q) => {
    let s = ` ${String(q)} `;
    for (const [k, v] of Object.entries(typos)) {
      if (k.includes(' ')) s = s.replace(new RegExp(`(\\s)${k}(?=\\s)`, 'gi'), `$1${v}`);
    }
    return s.trim().split(/\s+/).map((w) => { const lw = w.toLowerCase(); return Object.prototype.hasOwnProperty.call(typos, lw) && !lw.includes(' ') ? typos[lw] : w; }).join(' ');
  };
  const norm = (q) => fixTypos(q).replace(/\barmour\b/gi, 'armor').replace(/\btipguards?\b/gi, 'tip guard');
  const scopedUrl = (q, scope) => {
    q = norm(q);
    if (scope === 'guide') return `${searchUrl}?type=article,page&q=${encodeURIComponent(q)}`;
    const s = SCOPES[scope] ? `${q} ${SCOPES[scope]}`.trim() : q;
    return `${searchUrl}?type=product&q=${encodeURIComponent(s)}`;
  };
  const tokens = (q) => {
    const t = q.toLowerCase().replace(/[^\p{L}\p{N}.\s]/gu, ' ').split(/\s+/).filter(Boolean);
    return (t.length > 1 ? t.filter((x) => x.length > 1) : t).sort((a, b) => b.length - a.length);
  };
  const reEsc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /* Matched text gets <mark>. Query suggestions bold the completion instead (Baymard). */
  const highlight = (scope, q) => {
    const toks = tokens(q);
    if (!toks.length) return;
    const re = new RegExp(`(${toks.map(reEsc).join('|')})`, 'gi');
    scope.querySelectorAll('[data-hl]').forEach((el) => {
      el.innerHTML = esc(el.textContent).replace(re, '<mark>$1</mark>');
    });
    scope.querySelectorAll('[data-complete]').forEach((el) => {
      el.innerHTML = el.textContent.split(/(\s+)/).map((w) => {
        if (/^\s+$/.test(w) || !w) return w;
        const t = toks.find((x) => w.toLowerCase().startsWith(x));
        return t ? esc(w.slice(0, t.length)) + (w.length > t.length ? `<b>${esc(w.slice(t.length))}</b>` : '') : `<b>${esc(w)}</b>`;
      }).join('');
    });
  };

  /* ---------- Results page ---------- */
  const sr = d.querySelector('[data-sr]');
  if (sr) {
    const terms = sr.dataset.terms || '';
    const scope = sr.dataset.scope || 'all';
    if (terms && Number(sr.dataset.count) > 0) recent.add(terms);
    const f = sr.querySelector('[data-sr-form]');
    if (f && SCOPES[scope]) {
      f.addEventListener('submit', (e) => {
        const q = f.querySelector('[data-sr-input]').value.trim();
        if (!q) return;
        e.preventDefault();
        location.href = scopedUrl(q, scope);
      });
    }
    const dym = sr.querySelector('[data-sr-dym]');
    if (dym && terms) {
      const u = new URL(`${suggestUrl}.json`, location.origin);
      u.searchParams.set('q', terms);
      u.searchParams.set('resources[type]', 'query');
      u.searchParams.set('resources[limit]', '4');
      fetch(u).then((r) => (r.ok ? r.json() : null)).then((j) => {
        const qs = (j && j.resources && j.resources.results && j.resources.results.queries) || [];
        const list = qs.filter((x) => x && typeof x.text === 'string' && x.text.toLowerCase() !== String(terms).toLowerCase()).slice(0, 4);
        if (!list.length) return;
        dym.querySelector('[data-sr-dym-list]').innerHTML = list.map((x) => `<a class="ps-chip" href="${esc(scopedUrl(x.text, 'all'))}">${esc(x.text)}</a>`).join(' ');
        dym.hidden = false;
      }).catch(() => {});
    }
  }

  /* "Compare in full" in the at-a-glance table: hand the sprayers' payloads to the compare tray (same key as compare.js) so
     the collection page can open them even when they are not on its first page. The link itself still carries ?compare=handles. */
  d.addEventListener('click', (e) => {
    const go = e.target.closest('[data-cmp-go]');
    if (!go) return;
    try { localStorage.setItem('ase.compare.v1', JSON.stringify(JSON.parse(go.dataset.cmpGo))); } catch (err) { /* link still works */ }
  });

  /* ---------- Palette ---------- */
  const root = d.querySelector('[data-ps]');
  if (!root) return;
  const panel = root.querySelector('[role="dialog"]');
  const form = root.querySelector('[data-ps-form]');
  const input = root.querySelector('[data-ps-input]');
  const list = root.querySelector('[data-ps-list]');
  const emptyPane = root.querySelector('[data-ps-empty]');
  const live = root.querySelector('[data-ps-live]');
  const status = root.querySelector('[data-ps-status]');
  const clearBtn = root.querySelector('[data-ps-clear]');
  const tabs = [...root.querySelectorAll('[data-scope]')];
  const recentBox = root.querySelector('[data-ps-recent]');
  const popular = root.querySelector('[data-ps-popular]');
  const cache = new Map();
  const redirects = new Map((root.dataset.redirects || '').split(/\n/).map((l) => l.split('=>').map((x) => x.trim())).filter((x) => x.length === 2 && x[0] && /^\//.test(x[1])).map(([k, v]) => [k.toLowerCase(), v]));
  let opener = null, ctrl = null, timer = 0, slow = 0, typed = '', scope = 'all', active = null, holdUntil = 0, closeT = 0;

  const isOpen = () => !root.hidden && root.classList.contains('is-open');
  const pane = () => (live.hidden ? emptyPane : live);
  const options = () => [...pane().querySelectorAll('[role="option"]')].filter((o) => !o.closest('[hidden]') && o.offsetParent !== null);

  const setActive = (el, fill) => {
    if (active) { active.classList.remove('is-active'); active.setAttribute('aria-selected', 'false'); }
    active = el || null;
    if (!active) { input.removeAttribute('aria-activedescendant'); return; }
    active.classList.add('is-active');
    active.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
    // Copy a focused suggestion into the field so it can be refined (Baymard).
    const t = active.dataset.text || active.dataset.psTerm;
    if (fill && t) input.value = t;
  };
  const move = (dir) => {
    const o = options();
    if (!o.length) return;
    const i = o.indexOf(active);
    const n = i < 0 ? (dir > 0 ? 0 : o.length - 1) : (i + dir + o.length) % o.length;
    setActive(o[n], true);
  };

  const renderRecent = () => {
    const a = recent.get();
    recentBox.hidden = !a.length;
    const box = recentBox.querySelector('[data-ps-recent-list]');
    box.innerHTML = a.map((q, i) => `<div class="ps-recent"><a class="ps-q" role="option" tabindex="-1" aria-selected="false" id="ps-opt-r${i}" href="${esc(scopedUrl(q, 'all'))}" data-ps-term="${esc(q)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/></svg><span class="ps-q__t">${esc(q)}</span></a><button type="button" class="ps-recent__x" data-ps-forget="${esc(q)}" aria-label="Remove ${esc(q)} from recent searches">×</button></div>`).join('');
  };

  const counts = () => {
    const c = { all: 0, sprayer: 0, tip: 0, part: 0, guide: 0 };
    live.querySelectorAll('[data-type="product"],[data-type="collection"],[data-kind="guide"][data-type]').forEach((o) => {
      c.all += 1;
      if (c[o.dataset.kind] !== undefined) c[o.dataset.kind] += 1;
    });
    return c;
  };
  const applyScope = () => {
    tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.scope === scope)));
    input.placeholder = scope === 'all' ? input.dataset.ph : `Search ${LABELS[scope]}`;
    if (live.hidden) return;
    const kindMatch = (k) => scope === 'all' || k === scope || k === 'all';
    live.querySelectorAll('[role="option"]').forEach((o) => { o.hidden = !kindMatch(o.dataset.kind); });
    live.querySelectorAll('[data-ps-intent]').forEach((c) => {
      const k = { part: 'part', 'part-missing': 'part', sprayer: 'sprayer', compare: 'sprayer', tip: 'tip', topics: 'guide' }[c.dataset.psIntent];
      // The compare table only has sprayer links, so it shows under All and Sprayers; the single sprayer card also carries tip and part links.
      c.hidden = c.dataset.psIntent === 'compare' ? !(scope === 'all' || scope === 'sprayer') : !(scope === 'all' || k === scope || (k === 'sprayer' && scope !== 'guide'));
    });
    live.querySelectorAll('.ps-group, .sa-tier, .sa-group, .sa__links').forEach((g) => { g.hidden = !g.querySelector('[role="option"]:not([hidden])'); });
    // A side column with nothing visible in it gives its width back to the results (no empty left column).
    live.querySelectorAll('.ps-cols').forEach((cols) => {
      const side = cols.querySelector('.ps-col--side');
      cols.classList.toggle('ps-cols--solo', !side || !side.querySelector('.ps-group:not([hidden])'));
      if (side) side.hidden = !side.querySelector('.ps-group:not([hidden])');
    });
    const all = live.querySelector('[data-ps-all]');
    const q = typed.trim();
    if (all) {
      all.href = scopedUrl(q, scope);
      all.firstElementChild.textContent = scope === 'all' ? `See all results for “${q}”` : `See all ${LABELS[scope]} for “${q}”`;
    }
    const se = live.querySelector('[data-ps-scope-empty]');
    if (se) {
      const visible = [...live.querySelectorAll('[data-type]:not([hidden])')].length;
      se.hidden = scope === 'all' || visible > 0;
      se.firstElementChild.textContent = `No ${LABELS[scope]} in the top matches. The full search goes deeper.`;
    }
    const c = counts();
    tabs.forEach((t) => { const n = t.querySelector('[data-n]'); n.textContent = c[t.dataset.scope] ? c[t.dataset.scope] : ''; });
    setActive(null);
  };

  const showEmpty = () => {
    live.hidden = true;
    live.innerHTML = '';
    emptyPane.hidden = false;
    clearBtn.hidden = true;
    tabs.forEach((t) => { t.querySelector('[data-n]').textContent = ''; });
    renderRecent();
    setActive(null);
    status.textContent = '';
  };

  const render = (html, q) => {
    const t = d.createElement('template');
    t.innerHTML = html;
    const res = t.content.querySelector('[data-ps-results]');
    if (!res || input.value.trim() !== q) return;
    // The best-match card already shows this product: drop its row from the list.
    res.querySelectorAll('[data-ps-intent] .si-hero, [data-ps-intent] [data-dedupe]').forEach((h) => {
      res.querySelectorAll(`.ps-row[href="${CSS.escape(h.getAttribute('href'))}"]`).forEach((r) => r.remove());
    });
    res.querySelectorAll('.ps-group[data-group="products"]').forEach((g) => { if (!g.querySelector('.ps-row')) g.remove(); });
    live.replaceChildren(res);
    emptyPane.hidden = true;
    live.hidden = false;
    highlight(res, q);
    const slot = res.querySelector('[data-ps-popular-slot]');
    if (slot && popular) slot.append(popular.cloneNode(true));
    slot && slot.querySelectorAll('[id]').forEach((el) => { el.id += '-z'; });
    applyScope();
    const total = Number(res.dataset.total || 0);
    const nTopics = res.querySelectorAll('[data-ps-intent="topics"] a').length;
    status.textContent = total ? `${total} results. Use up and down arrows to review.` : (nTopics ? `No products for ${q}. ${nTopics} guides and collections that may help.` : `No results for ${q}.`);
    const intent = res.querySelector('[data-ps-intent]');
    if (intent) track('search_intent', { search_term: q, intent: intent.dataset.psIntent });
  };

  const fetchResults = (q) => {
    if (cache.has(q)) { render(cache.get(q), q); return; }
    if (Date.now() < holdUntil) return;
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();
    const u = new URL(suggestUrl, location.origin);
    u.searchParams.set('q', norm(q));
    u.searchParams.set('section_id', 'predictive-search');
    u.searchParams.set('resources[type]', 'product,collection,page,article,query');
    u.searchParams.set('resources[limit]', '10');
    u.searchParams.set('resources[limit_scope]', 'each');
    u.searchParams.set('resources[options][unavailable_products]', 'last');
    u.searchParams.set('resources[options][fields]', 'title,product_type,variants.sku,variants.title,vendor,tag');
    clearTimeout(slow);
    slow = setTimeout(() => root.classList.add('is-loading'), 250);
    fetch(u, { signal: ctrl.signal, headers: { Accept: 'text/html' } })
      .then((r) => {
        if (r.status === 429) { holdUntil = Date.now() + (Number(r.headers.get('Retry-After')) || 2) * 1000; throw new Error('throttled'); }
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((html) => {
        cache.set(q, html);
        if (cache.size > 30) cache.delete(cache.keys().next().value);
        render(html, q);
      })
      .catch(() => {})
      .finally(() => { clearTimeout(slow); root.classList.remove('is-loading'); });
  };

  const onInput = (now) => {
    typed = input.value;
    const q = typed.trim();
    clearBtn.hidden = !typed;
    clearTimeout(timer);
    if (!q) { if (ctrl) ctrl.abort(); showEmpty(); return; }
    timer = setTimeout(() => fetchResults(q), now === true ? 0 : 140);
  };

  const lock = (on) => {
    const h = d.documentElement;
    if (on) {
      const sbw = window.innerWidth - h.clientWidth;
      h.style.setProperty('--ps-sbw', `${sbw}px`);
      h.classList.add('ps-lock');
    } else {
      h.classList.remove('ps-lock');
      h.style.removeProperty('--ps-sbw');
    }
  };

  const open = (prefill) => {
    if (isOpen()) { input.focus(); return; }
    clearTimeout(closeT);
    opener = d.activeElement;
    root.hidden = false;
    lock(true);
    input.focus({ preventScroll: true });
    requestAnimationFrame(() => root.classList.add('is-open'));
    if (typeof prefill === 'string' && prefill) { input.value = prefill; onInput(true); }
    else if (!input.value) showEmpty();
    else input.select();
  };
  const close = () => {
    if (root.hidden) return;
    root.classList.remove('is-open');
    lock(false);
    if (ctrl) ctrl.abort();
    setActive(null);
    const done = () => { root.hidden = true; };
    if (reduced()) done(); else closeT = setTimeout(done, 200);
    if (opener && opener.focus) opener.focus({ preventScroll: true });
  };

  input.dataset.ph = input.placeholder;
  input.addEventListener('input', onInput);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter' && active) { e.preventDefault(); active.click(); }
    else if (e.key === 'Escape') {
      e.preventDefault();
      if (input.value) { input.value = ''; onInput(); } else close();
    }
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) return;
    recent.add(q);
    track('search', { search_term: q, search_scope: scope });
    // Merchandised redirects (theme setting), then a part number with an exact match: go straight there.
    const to = redirects.get(q.toLowerCase());
    if (to) { location.href = to; return; }
    const exact = !live.hidden && live.querySelector('[data-ps-exact]');
    if (exact && q === typed.trim()) { location.href = exact.href; return; }
    location.href = scopedUrl(q, scope);
  });
  clearBtn.addEventListener('click', () => { input.value = ''; onInput(); input.focus(); });

  tabs.forEach((t, i) => {
    t.addEventListener('click', () => { scope = t.dataset.scope; applyScope(); input.focus(); });
    t.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      n.focus(); n.click(); n.focus();
    });
  });

  list.addEventListener('mousemove', (e) => {
    const o = e.target.closest('[role="option"]');
    if (o && o !== active) setActive(o, false);
  });
  list.addEventListener('click', (e) => {
    const cx = e.target.closest('[data-cx-toggle]');
    if (cx) {
      // One line in the overlay; tapping opens the strip in place. Only an open strip's links are keyboard options.
      const body = cx.closest('[data-cmp-fold]').querySelector('[data-cx-body]');
      const open = body.hidden;
      body.hidden = !open;
      cx.setAttribute('aria-expanded', String(open));
      cx.closest('[data-cmp-fold]').classList.toggle('is-open', open);
      body.querySelectorAll('[data-cx-opt]').forEach((a) => { if (open) a.setAttribute('role', 'option'); else a.removeAttribute('role'); });
      if (open) cx.scrollIntoView({ block: 'nearest' });
      return;
    }
    const forget = e.target.closest('[data-ps-forget]');
    if (forget) { recent.remove(forget.dataset.psForget); renderRecent(); input.focus(); return; }
    const term = e.target.closest('[data-ps-term]');
    if (term && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      input.value = term.dataset.psTerm;
      onInput(true);
      input.focus();
      return;
    }
    const o = e.target.closest('a[href]');
    if (!o) return;
    const q = (typed || input.value).trim();
    if (q) recent.add(q);
    track('search_suggest_select', { search_term: q, item_type: o.dataset.type || o.dataset.kind || 'link', item_url: o.getAttribute('href') });
    if (o.hasAttribute('data-chat-open') && window.ASE && ASE.chat) close();
  });
  root.querySelector('[data-ps-recent-clear]').addEventListener('click', () => { recent.set([]); renderRecent(); input.focus(); });
  root.querySelectorAll('[data-ps-close]').forEach((b) => b.addEventListener('click', close));

  // Keep Tab inside the dialog.
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const f = [...panel.querySelectorAll('input:not([type="hidden"]), button, [href]:not([tabindex="-1"])')].filter((x) => !x.hidden && x.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  d.addEventListener('click', (e) => {
    const t = e.target.closest('[data-search-open]');
    if (!t || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    open(t.dataset.searchOpen || '');
  });
  d.addEventListener('keydown', (e) => {
    const k = (e.key || '').toLowerCase(); // autofill fires keydown with no key
    if ((e.metaKey || e.ctrlKey) && k === 'k') { e.preventDefault(); isOpen() ? close() : open(); return; }
    if (e.key === 'Escape' && isOpen() && d.activeElement !== input) { close(); return; }
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isOpen()) return;
    const a = d.activeElement;
    if (a && (a.isContentEditable || /^(input|textarea|select)$/i.test(a.tagName))) return;
    e.preventDefault();
    const page = d.querySelector('[data-sr-input]');
    if (page) { page.focus(); page.select(); } else open();
  });
  window.ASE = Object.assign(window.ASE || {}, { search: { open, close } });
})();
